import { useState } from 'react'
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { learningApi } from '../api/learningApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import type { VerificationStatus } from '../types/learning.types'

const STATUS_STYLE: Record<VerificationStatus, { bg: string; dot: string; label: string }> = {
  pending:  { bg: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-400', label: 'Pending' },
  verified: { bg: 'bg-green-100  text-green-700',  dot: 'bg-green-500',  label: 'Verified' },
  rejected: { bg: 'bg-red-100    text-red-700',    dot: 'bg-red-500',    label: 'Rejected' },
}

const TYPE_LABEL: Record<string, string> = {
  course: 'Course', workshop: 'Workshop', certification: 'Certification',
  webinar: 'Webinar', seminar: 'Seminar', conference: 'Conference', other: 'Other',
}

type Tab = 'history' | 'certifications'

function StatusBadge({ status }: { status: VerificationStatus }) {
  const st = STATUS_STYLE[status] ?? STATUS_STYLE.pending
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${st.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot}`} />
      {st.label}
    </span>
  )
}

const EmployeeLearningProfilePage = () => {
  const { employeeId } = useParams<{ employeeId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const navState = location.state as { employeeName?: string; department?: string; email?: string } | null

  const isAdminHr =
    user?.roles?.some(r => ['admin', 'hr'].includes(r.name.toLowerCase())) ||
    user?.division_name?.toLowerCase().includes('human')

  const [activeTab, setActiveTab]       = useState<Tab>('history')
  const [rejectingId, setRejectingId]   = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['employeeRecords', employeeId],
    queryFn: () => learningApi.getAllRecords({ employee_id: employeeId, limit: 200 }),
    enabled: !!employeeId && !!isAdminHr,
  })

  const records = (data?.data ?? []).slice().sort((a, b) =>
    new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  )

  const certRecords = records
    .filter(r => !!r.certification_date || r.training?.training_type === 'certification')
    .slice()
    .sort((a, b) => {
      const da = a.certification_date ? new Date(a.certification_date).getTime() : 0
      const db = b.certification_date ? new Date(b.certification_date).getTime() : 0
      return db - da
    })

  const firstEmployee = records[0]?.employee
  const employeeName  = firstEmployee?.full_name || navState?.employeeName || 'Employee'
  const department    = firstEmployee?.department  || navState?.department  || ''
  const email         = firstEmployee?.email        || navState?.email       || ''

  const totalVerified = records.filter(r => r.verification_status === 'verified').length
  const totalPending  = records.filter(r => r.verification_status === 'pending').length
  const totalRejected = records.filter(r => r.verification_status === 'rejected').length

  const verifyMutation = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: 'verify' | 'reject'; reason?: string }) =>
      learningApi.verifyRecord(id, action, reason),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['employeeRecords', employeeId] })
      toast.success(vars.action === 'verify' ? 'Record verified' : 'Record rejected')
      setRejectingId(null)
      setRejectReason('')
    },
    onError: () => toast.error('Action failed. Please try again.'),
  })

  const handleReject = (id: string) => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return }
    verifyMutation.mutate({ id, action: 'reject', reason: rejectReason })
  }

  if (!isAdminHr) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to view employee learning profiles.</p>
          <Link to="/learning/reports" className="mt-4 inline-block text-red-600 hover:text-red-700">
            &larr; Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  if (isLoading) return <LoadingPage />

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to load records</h2>
          <p className="text-gray-600 mb-4">Could not fetch training records for this employee.</p>
          <Link to="/learning/employees" className="text-red-600 hover:text-red-700">
            &larr; Back to By Employee
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm mb-4">
            <Link to="/learning" className="text-red-100 hover:text-white">Learning</Link>
            <span className="text-red-300">/</span>
            <Link to="/learning/reports" className="text-red-100 hover:text-white">Dashboard</Link>
            <span className="text-red-300">/</span>
            <span className="text-white">{employeeName}</span>
          </nav>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white/20 ring-2 ring-white/30 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold">{employeeName.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold">{employeeName}</h1>
                <p className="text-red-100 mt-0.5 text-sm">
                  {department && <span>{department}</span>}
                  {department && email && <span className="mx-2 text-red-300">·</span>}
                  {email && <span>{email}</span>}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(
                `/learning/records/new?for=${employeeId}`,
                { state: { employeeName } }
              )}
              className="flex items-center gap-2 px-4 py-2 bg-white text-red-700 rounded-xl font-medium text-sm hover:bg-red-50 transition-colors shadow-sm flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Record
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Records',  value: records.length,   textColor: 'text-gray-900',  bg: 'bg-white',    border: 'border-gray-200' },
            { label: 'Verified',       value: totalVerified,    textColor: 'text-green-700', bg: 'bg-green-50', border: 'border-green-100' },
            { label: 'Pending Review', value: totalPending,     textColor: totalPending  > 0 ? 'text-amber-700' : 'text-gray-400', bg: totalPending  > 0 ? 'bg-amber-50' : 'bg-white', border: totalPending  > 0 ? 'border-amber-100' : 'border-gray-200' },
            { label: 'Rejected',       value: totalRejected,    textColor: totalRejected > 0 ? 'text-red-700'   : 'text-gray-400', bg: 'bg-white',    border: 'border-gray-200' },
            { label: 'Certifications', value: certRecords.length, textColor: certRecords.length > 0 ? 'text-purple-700' : 'text-gray-400', bg: certRecords.length > 0 ? 'bg-purple-50' : 'bg-white', border: certRecords.length > 0 ? 'border-purple-100' : 'border-gray-200' },
          ].map(card => (
            <div key={card.label} className={`${card.bg} ${card.border} border rounded-xl p-4`}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 leading-tight">{card.label}</p>
              <p className={`text-2xl font-bold ${card.textColor}`}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Pending alert */}
        {totalPending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-sm text-amber-900 font-medium">
              {totalPending} training {totalPending === 1 ? 'record needs' : 'records need'} verification
            </p>
          </div>
        )}

        {/* Tabbed card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Tab bar */}
          <div className="flex items-center justify-between px-6 border-b border-gray-200">
            <div className="flex -mb-px">
              {([
                { id: 'history',       label: 'Training History',  count: records.length },
                { id: 'certifications', label: 'Certifications',   count: certRecords.length },
              ] as { id: Tab; label: string; count: number }[]).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-red-600 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                    activeTab === tab.id
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Training History tab ── */}
          {activeTab === 'history' && (
            records.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 font-medium">No training records</p>
                <p className="text-gray-400 text-sm mt-1">This employee has not submitted any training records yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/60">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Training</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Certification</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(record => {
                      const isRejecting = rejectingId === record.id
                      const expired = record.certification_expiry_date
                        ? new Date(record.certification_expiry_date) < new Date() : false
                      const expiringSoon = !expired && record.certification_expiry_date
                        ? (new Date(record.certification_expiry_date).getTime() - Date.now()) < 60 * 24 * 60 * 60 * 1000 : false

                      return (
                        <tr key={record.id} className={`border-b border-gray-100 ${isRejecting ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}>
                          <td className="py-3.5 px-4 max-w-xs">
                            <Link to={`/learning/records/${record.id}`} className="text-sm font-medium text-gray-900 hover:text-red-600 block truncate">
                              {record.training?.name ?? '—'}
                            </Link>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {record.training?.training_type && (
                                <span className="text-xs text-gray-400">{TYPE_LABEL[record.training.training_type] ?? record.training.training_type}</span>
                              )}
                              {record.training?.provider && <span className="text-xs text-gray-400">· {record.training.provider}</span>}
                              {record.training?.duration_hours && <span className="text-xs text-gray-400">· {record.training.duration_hours}h</span>}
                              {record.has_certificate && (
                                <span className="inline-flex items-center gap-0.5 text-xs text-purple-600">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  File
                                </span>
                              )}
                            </div>
                            {isRejecting && (
                              <div className="mt-2 flex items-center gap-2">
                                <input
                                  type="text"
                                  value={rejectReason}
                                  onChange={e => setRejectReason(e.target.value)}
                                  placeholder="Rejection reason (required)…"
                                  className="flex-1 px-2.5 py-1.5 text-xs border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                  autoFocus
                                  onKeyDown={e => e.key === 'Enter' && handleReject(record.id)}
                                />
                                <button
                                  onClick={() => handleReject(record.id)}
                                  disabled={verifyMutation.isPending}
                                  className="px-2.5 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => { setRejectingId(null); setRejectReason('') }}
                                  className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-sm text-gray-600 whitespace-nowrap">
                            {record.start_date ? format(new Date(record.start_date), 'dd MMM yyyy') : '—'}
                            {record.end_date && (
                              <span className="text-gray-400"> → {format(new Date(record.end_date), 'dd MMM yyyy')}</span>
                            )}
                          </td>
                          <td className="py-3.5 px-4">
                            {record.certification_date ? (
                              <div>
                                <p className="text-sm text-gray-700">{format(new Date(record.certification_date), 'dd MMM yyyy')}</p>
                                {record.certification_expiry_date ? (
                                  <p className={`text-xs mt-0.5 font-medium ${expired ? 'text-red-600' : expiringSoon ? 'text-amber-600' : 'text-gray-400'}`}>
                                    Exp: {format(new Date(record.certification_expiry_date), 'dd MMM yyyy')}
                                    {expired && ' · expired'}{!expired && expiringSoon && ' · expiring soon'}
                                  </p>
                                ) : (
                                  <p className="text-xs text-gray-400 mt-0.5">No expiry</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3.5 px-4">
                            <StatusBadge status={record.verification_status} />
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center justify-end gap-1">
                              {record.verification_status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => verifyMutation.mutate({ id: record.id, action: 'verify' })}
                                    disabled={verifyMutation.isPending}
                                    className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50"
                                  >
                                    Verify
                                  </button>
                                  <button
                                    onClick={() => { setRejectReason(''); setRejectingId(isRejecting ? null : record.id) }}
                                    disabled={verifyMutation.isPending}
                                    className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              <Link
                                to={`/learning/records/${record.id}`}
                                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                              >
                                Edit
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* ── Certifications tab ── */}
          {activeTab === 'certifications' && (
            certRecords.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <p className="text-gray-500 font-medium">No certifications</p>
                <p className="text-gray-400 text-sm mt-1">
                  No records with certification type or certification date.
                  {records.length > 0 && ` (${records.length} other training record${records.length !== 1 ? 's' : ''} in history)`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/60">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Training / Provider</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Certified On</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Expiry</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Certificate</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certRecords.map(record => {
                      const expired = record.certification_expiry_date
                        ? new Date(record.certification_expiry_date) < new Date() : false
                      const expiringSoon = !expired && record.certification_expiry_date
                        ? (new Date(record.certification_expiry_date).getTime() - Date.now()) < 60 * 24 * 60 * 60 * 1000 : false

                      return (
                        <tr key={record.id} className="border-b border-gray-100 hover:bg-purple-50/30">
                          <td className="py-3.5 px-4 max-w-xs">
                            <Link to={`/learning/records/${record.id}`} className="text-sm font-medium text-gray-900 hover:text-red-600 block truncate">
                              {record.training?.name ?? '—'}
                            </Link>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {record.training?.training_type && (
                                <span className="text-xs text-gray-400">{TYPE_LABEL[record.training.training_type] ?? record.training.training_type}</span>
                              )}
                              {record.training?.provider && <span className="text-xs text-gray-400">· {record.training.provider}</span>}
                            </div>
                          </td>

                          <td className="py-3.5 px-4 text-sm text-gray-700 whitespace-nowrap">
                            {record.certification_date
                              ? format(new Date(record.certification_date), 'dd MMM yyyy')
                              : '—'}
                          </td>

                          <td className="py-3.5 px-4 whitespace-nowrap">
                            {record.certification_expiry_date ? (
                              <div>
                                <span className={`text-sm font-medium ${expired ? 'text-red-600' : expiringSoon ? 'text-amber-600' : 'text-green-700'}`}>
                                  {format(new Date(record.certification_expiry_date), 'dd MMM yyyy')}
                                </span>
                                {(expired || expiringSoon) && (
                                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${expired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {expired ? 'Expired' : 'Expiring soon'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No expiry</span>
                            )}
                          </td>

                          <td className="py-3.5 px-4">
                            {record.has_certificate ? (
                              <Link
                                to={`/learning/records/${record.id}`}
                                className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                View file
                              </Link>
                            ) : (
                              <span className="text-xs text-gray-400">No file</span>
                            )}
                          </td>

                          <td className="py-3.5 px-4">
                            <StatusBadge status={record.verification_status} />
                          </td>

                          <td className="py-3.5 px-4">
                            <div className="flex items-center justify-end gap-1">
                              {record.verification_status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => verifyMutation.mutate({ id: record.id, action: 'verify' })}
                                    disabled={verifyMutation.isPending}
                                    className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50"
                                  >
                                    Verify
                                  </button>
                                  <button
                                    onClick={() => {
                                      setActiveTab('history')
                                      setTimeout(() => setRejectingId(record.id), 50)
                                    }}
                                    disabled={verifyMutation.isPending}
                                    className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              <Link
                                to={`/learning/records/${record.id}`}
                                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                              >
                                Edit
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 text-right px-6 py-3">
                  {certRecords.length} certification{certRecords.length !== 1 ? 's' : ''}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployeeLearningProfilePage
